<script setup>
    import { useDebounceFn } from '@vueuse/core';
    const { loggedIn, user, fetch: refreshSession } = useUserSession()
    import { close_button_ui_styles, modal_ui_styles } from '@/utilities/ui';    
    import { useWaitingMessages } from '@/composables/helpers/useWaitingMessages';

    const { $bus } = useNuxtApp();

    const open = defineModel('open');
    const router = useRouter();
    const route = useRoute();
    const confirm = ref(false);

    const props = defineProps({
        session_id: {
            type: String,
            default: '',
            required: true
        }
    });

    const { messages: waiting, getMessage } = useWaitingMessages();

    const message = ref({})
    const count = ref(0);
    const isRegistering = ref(false);

    const { data, pending, error, refresh } = await useAsyncData(
        `session-${props.session_id}`,
        () => $fetch(`/api/v1/sessions`, { params: { session: props.session_id } })
    );

    watch(
        () => props.session_id,
        () => {
            refresh();
        }
    );

    const unregisterForSession = useDebounceFn(async () => {
        if (!props.session_id) return;
        isRegistering.value = true;
        try {
            const now = Date.now();
            message.value = await $fetch(`/api/v2/reservations/unregister?ts=${now}&u=${user.value.ticketId}`, {
                method: 'POST',
                body: { session_id: props.session_id, ts: Date.now() }
            });
            await refreshSession()
            $bus.emit('sessions:unregistered', props.session_id);
        } catch (e) {
            console.error('Error registering for session:', e);
        } finally {
            isRegistering.value = false;
        }
    }, 2500);

    const handleConfirm = async () => {
        if (!props.session_id) return;
        confirm.value = true;
        isRegistering.value = true;
        unregisterForSession()
    };

    const handleSeeSeats = async () => {
        await refreshSession()
        await navigateTo({ path: '/sessions', query: { tab: 'seats', view: 'list' } }, { replace: true });
        setTimeout(() => {
            open.value = false;
        }, 1000);
    };

    onMounted(() => {
        const interval = setInterval(() => {
            count.value = (count.value + 1) % waiting.length;
        }, 10000);
        onUnmounted(() => {
            clearInterval(interval);
        });
    });

    watch(open, (val) => {
        if (val) {
            $bus.emit('drawer-open');
        } else {
            $bus.emit('drawer-close');
            const currentQuery = { ...route.query };
            delete currentQuery.modal;
            router.replace({ query: currentQuery });
            confirm.value = false;
            isRegistering.value = false;
            message.value = {};
        }
    });
</script>

<template>
    <UModal
        v-model:open="open"
        title="Unreserve a Seat"
        :ui="modal_ui_styles"
        :dismissable="false"
        :close="close_button_ui_styles"
        class="reservations-modal leave-modal">
        <template #body>
            <div class="flex flex-col items-center justify-center inner" v-if="!confirm && !isRegistering && !pending && data">
                <h1 class="h1">Are you sure you want to remove your saved seat for "{{ data.title }}"?</h1>
                <div class="warning-banner">
                    <UIcon name="local-warning" class="warning-icon" />
                    <p>This session has a limited number of seats available and requires a seat reservation to attend.</p>
                </div>
                <div class="actions">
                    <UButton label="Cancel" variant="outline-close" color="orange" @click="open = false" />
                    <UButton label="Yes, Remove Saved Seat" variant="outline-close" @click="handleConfirm" />
                </div>
            </div>
            <div class="flex flex-col items-center justify-center inner" v-if="!isRegistering && confirm && message">
                <h1 class="h1" v-html="message.title"></h1>
                <div class="warning-banner" v-if="message.copy" :class="message.type">
                    <UIcon name="local-warning" class="warning-icon" />
                    <p v-html="message.copy"></p>
                </div>
                <div class="actions">
                    <UButton label="Close" variant="outline-close" color="orange" @click="open = false" />
                    <UButton label="View My Saved Seats" v-if="message.type === 'success'" variant="outline-close" @click="handleSeeSeats" />
                </div>
            </div>
            <div class="flex flex-col items-center justify-center inner spinner" v-if="isRegistering && confirm">
                <FragmentsSpinner />
                <h1 class="h1">Working on your request!</h1>
                <div class="warning-banner">
                    <UIcon name="local-warning" class="warning-icon" />
                    <p>Please don't close this window or we might not complete your request!</p>
                </div>
                <p class="spin-text">{{ getMessage(count) }}</p>
            </div>
        </template>
    </UModal>
</template>

<style lang="scss" scoped>
    .reservations-modal {
        color: var(--teal-13);

        .inner {
            max-width: 41.875rem;
            width: 100%;
            padding-bottom: 2.5rem;

            &.spinner {
                gap: 1.6875rem;

                .warning-banner {
                    margin-bottom: 0;
                }

                .h1 {
                    margin-bottom: 0;
                }

                .spin-text {
                    color: var(--teal);
                    font-size: 1.0625rem;
                    font-style: normal;
                    font-weight: 300;
                    line-height: 1.75rem;
                    letter-spacing: 0.02125rem;
                }
            }

            .actions {
                display: flex;
                flex-direction: row;
                gap: 1.5625rem;
            }

            h1 {
                text-align: center;
                font-size: 2rem;
                font-style: normal;
                font-weight: 500;
                line-height: 2.5rem;
                letter-spacing: -0.04rem;
                margin-bottom: 2rem;
                text-wrap: balance;
            }

            .warning-icon {
                width: 1.5rem;
                height: 1.5rem;
                flex-shrink: 0;
            }

            .warning-banner,
            .success {
                background-color: var(--orange-ghost);
                border-radius: var(--radius);
                padding: 1.8125rem;
                color: var(--orange-ghost-dark);
                font-size: 0.875rem;
                font-style: normal;
                font-weight: 400;
                line-height: 1.25rem;
                display: flex;
                flex-direction: column;
                gap: 1rem;
                margin-bottom: 2.8125rem;
                letter-spacing: 0;
                width: 100%;

                @media (min-width: $tablet) {
                    flex-direction: row;
                    align-items: center;
                    gap: 1.644375rem;
                }
            }
        }
    }
</style>
